import React, { useState, useEffect, useRef } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { MdSearch, MdClose, MdShoppingCart, MdDescription } from 'react-icons/md';
import { useDebounce } from '../hooks/useDebounce';
import { getTranslatedValue, Translations } from './common/TranslatedValue';

// Define GraphQL query for global search
const GLOBAL_SEARCH = gql`
  query GlobalSearch($search: String!) {
    ads(search: $search, page: 1, pageSize: 10) {
      nodes {
        id
        title
        mainImage {
          url
        }
      }
      total
    }
    orders(filters: { search: $search }, page: 1, pageSize: 10) {
      nodes {
        id
        orderId
        customerName
        shop {
          shopType
        }
      }
      total
    }
  }
`;

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [activeSection, setActiveSection] = useState<'ads' | 'orders'>('ads');

  const [search, { loading, data }] = useLazyQuery(GLOBAL_SEARCH, {
    fetchPolicy: 'network-only',
  });

  // Focus input when overlay opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    } else {
      setSearchTerm('');
      setActiveIndex(-1);
    }
  }, [isOpen]);

  // Trigger search when search term changes
  useEffect(() => {
    if (debouncedSearchTerm.length > 1) {
      search({ variables: { search: debouncedSearchTerm } });
    }
  }, [debouncedSearchTerm, search]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
        e.preventDefault();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        
        // Calculate total items in current section
        const adsCount = data?.ads?.nodes?.length || 0;
        const ordersCount = data?.orders?.nodes?.length || 0;
        
        if (activeSection === 'ads') {
          if (activeIndex < adsCount - 1) {
            setActiveIndex(activeIndex + 1);
          } else if (ordersCount > 0) {
            setActiveSection('orders');
            setActiveIndex(0);
          }
        } else if (activeSection === 'orders') {
          if (activeIndex < ordersCount - 1) {
            setActiveIndex(activeIndex + 1);
          }
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        
        if (activeSection === 'orders') {
          if (activeIndex > 0) {
            setActiveIndex(activeIndex - 1);
          } else if (data?.ads?.nodes?.length > 0) {
            setActiveSection('ads');
            setActiveIndex(data.ads.nodes.length - 1);
          }
        } else if (activeSection === 'ads') {
          if (activeIndex > 0) {
            setActiveIndex(activeIndex - 1);
          }
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        
        if (activeSection === 'ads' && activeIndex >= 0 && data?.ads?.nodes[activeIndex]) {
          navigateToAd(data.ads.nodes[activeIndex].id);
        } else if (activeSection === 'orders' && activeIndex >= 0 && data?.orders?.nodes[activeIndex]) {
          navigateToOrder(data.orders.nodes[activeIndex].id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeIndex, activeSection, data, onClose]);

  const navigateToAd = (id: string) => {
    navigate(`/ad/${id}/edit`);
    onClose();
  };

  const navigateToOrder = (id: string) => {
    navigate(`/orders/${id}`);
    onClose();
  };

  const handleClickOutside = (e: React.MouseEvent) => {
    if (overlayRef.current && e.target === overlayRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const adsResults = data?.ads?.nodes || [];
  const ordersResults = data?.orders?.nodes || [];
  const hasResults = adsResults.length > 0 || ordersResults.length > 0;
  const showNoResults = debouncedSearchTerm.length > 1 && !loading && !hasResults;

  return (
    <div 
      ref={overlayRef}
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center pt-20"
      onClick={handleClickOutside}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Search input */}
        <div className="p-4 border-b border-gray-200 flex items-center gap-3">
          <MdSearch className="text-gray-500 text-xl" />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search for ads or orders..."
            className="flex-1 outline-hidden text-lg"
            autoFocus
          />
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <MdClose className="text-xl" />
          </button>
        </div>

        {/* Results container */}
        <div className="overflow-y-auto flex-1">
          {/* Loading state */}
          {loading && (
            <div className="p-4 text-center text-gray-500">
              Searching...
            </div>
          )}

          {/* No results */}
          {showNoResults && (
            <div className="p-4 text-center text-gray-500">
              No results found for "{debouncedSearchTerm}"
            </div>
          )}

          {/* Results sections */}
          {!loading && hasResults && (
            <div>
              {/* Ads section */}
              {adsResults.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-gray-100 text-sm font-semibold text-gray-700 flex justify-between">
                    <span>Ads</span>
                    {data?.ads?.total > 10 && (
                      <span className="text-blue-600 cursor-pointer" onClick={() => {
                        navigate(`/?search=${encodeURIComponent(debouncedSearchTerm)}`);
                        onClose();
                      }}>
                        View all ({data?.ads?.total})
                      </span>
                    )}
                  </div>
                  <ul>
                    {adsResults.map((ad, index) => {
                      const adTitle = getTranslatedValue(ad.title as unknown as Translations, 'FR');

                      return (
                      <li 
                        key={ad.id}
                        className={`px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 border-l-2 ${
                          activeSection === 'ads' && activeIndex === index 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-transparent'
                        }`}
                        onClick={() => navigateToAd(ad.id)}
                        onMouseEnter={() => {
                          setActiveSection('ads');
                          setActiveIndex(index);
                        }}
                      >
                        <div className="bg-blue-100 text-blue-800 p-2 rounded-full">
                          {ad.mainImage?.url && (
                            <img
                              src={`/img/w_32,h_32/${ad.mainImage?.url}`}
                              alt={adTitle}
                              className="w-10 h-10 object-cover"
                            />
                          )}
                        </div>
                        <div className="flex-1 truncate">
                          <div className="font-medium">{adTitle}</div>
                          <div className="text-sm text-gray-500">ID: {ad.id}</div>
                        </div>
                      </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Orders section */}
              {ordersResults.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-gray-100 text-sm font-semibold text-gray-700 flex justify-between">
                    <span>Orders</span>
                    {data?.orders?.total > 10 && (
                      <span className="text-blue-600 cursor-pointer" onClick={() => {
                        navigate(`/orders?search=${encodeURIComponent(debouncedSearchTerm)}`);
                        onClose();
                      }}>
                        View all ({data?.orders?.total})
                      </span>
                    )}
                  </div>
                  <ul>
                    {ordersResults.map((order, index) => (
                      <li 
                        key={order.id}
                        className={`px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 border-l-2 ${
                          activeSection === 'orders' && activeIndex === index 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-transparent'
                        }`}
                        onClick={() => navigateToOrder(order.id)}
                        onMouseEnter={() => {
                          setActiveSection('orders');
                          setActiveIndex(index);
                        }}
                      >
                        <div className="bg-green-100 text-green-800 p-2 rounded-full">
                          <MdShoppingCart />
                        </div>
                        <div className="flex-1 truncate">
                          <div className="font-medium">
                            {order.customerName}
                          </div>
                          <div className="text-sm text-gray-500">
                            Order #{order.orderId} ({order.shop.shopType})
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Initial state - empty search */}
          {!debouncedSearchTerm && !loading && (
            <div className="p-8 text-center text-gray-500">
              <MdSearch className="mx-auto text-5xl mb-4 text-gray-300" />
              <p className="text-lg">Type to search for ads or orders</p>
              <p className="text-sm mt-2">Use arrow keys to navigate and Enter to select</p>
            </div>
          )}
        </div>

        {/* Keyboard shortcuts help */}
        <div className="px-4 py-3 border-t border-gray-200 text-sm text-gray-500 flex justify-between">
          <div>
            <span className="inline-block bg-gray-200 rounded-sm px-2 py-1 mr-1">↑</span>
            <span className="inline-block bg-gray-200 rounded-sm px-2 py-1 mr-1">↓</span>
            to navigate
          </div>
          <div>
            <span className="inline-block bg-gray-200 rounded-sm px-2 py-1 mr-1">Enter</span>
            to select
          </div>
          <div>
            <span className="inline-block bg-gray-200 rounded-sm px-2 py-1 mr-1">Esc</span>
            to close
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch; 